/**
 * DatePicker — input de data com calendário (popover), tema escuro do painel.
 * Componentizado pra reuso. Retorna/recebe string 'YYYY-MM-DD' (date-only).
 *
 *   <DatePicker value={date} onChange={setDate} max={todayISO()} />
 */
import { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const WD = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const pad = (n: number) => String(n).padStart(2, '0');
const toISO = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || '');
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
export const todayISO = () => toISO(new Date());

interface DatePickerProps {
  value: string;                 // 'YYYY-MM-DD' | ''
  onChange: (v: string) => void;
  placeholder?: string;
  max?: string;                  // data máxima selecionável (ex: hoje)
  min?: string;                  // data mínima
  className?: string;
}

export function DatePicker({ value, onChange, placeholder = 'Selecionar data', max, min, className = '' }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parseISO(value);
  const maxDate = parseISO(max || '');
  const minDate = parseISO(min || '');
  const [view, setView] = useState<Date>(() => selected || new Date());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const y = view.getFullYear();
  const m = view.getMonth();
  const firstDow = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const disabled = (d: number) => {
    const dt = new Date(y, m, d);
    if (maxDate && dt > maxDate) return true;
    if (minDate && dt < minDate) return true;
    return false;
  };
  const isSel = (d: number) =>
    !!selected && selected.getFullYear() === y && selected.getMonth() === m && selected.getDate() === d;

  const label = selected
    ? `${pad(selected.getDate())}/${pad(selected.getMonth() + 1)}/${selected.getFullYear()}`
    : '';

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2
                   text-sm text-left hover:border-white/[0.15] transition cursor-pointer"
      >
        <Calendar size={15} className="text-white/40 shrink-0" />
        <span className={label ? 'text-white' : 'text-white/35'}>{label || placeholder}</span>
      </button>

      {open && (
        <div className="absolute z-[60] mt-1 p-3 rounded-xl bg-[#0d141b] border border-white/10 shadow-2xl w-[264px]">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => setView(new Date(y, m - 1, 1))}
              className="p-1 rounded hover:bg-white/[0.06] text-white/70 cursor-pointer"><ChevronLeft size={16} /></button>
            <span className="text-sm font-semibold text-white">{MONTHS[m]} {y}</span>
            <button type="button" onClick={() => setView(new Date(y, m + 1, 1))}
              className="p-1 rounded hover:bg-white/[0.06] text-white/70 cursor-pointer"><ChevronRight size={16} /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WD.map((w, i) => <div key={i} className="text-[10px] text-white/35 text-center font-bold">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => d === null ? <div key={i} /> : (
              <button
                key={i} type="button" disabled={disabled(d)}
                onClick={() => { onChange(toISO(new Date(y, m, d))); setOpen(false); }}
                className={`h-7 rounded text-xs cursor-pointer transition
                  ${isSel(d) ? 'bg-[#00e1d7] text-black font-bold' : 'text-white/75 hover:bg-white/[0.08]'}
                  ${disabled(d) ? 'opacity-25 cursor-not-allowed hover:bg-transparent' : ''}`}
              >
                {d}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { onChange(todayISO()); setView(new Date()); setOpen(false); }}
            className="mt-2 w-full text-[11px] text-[#00e1d7] hover:underline cursor-pointer"
          >
            Hoje
          </button>
        </div>
      )}
    </div>
  );
}
